import React, { useState, useEffect, useRef } from "react";
import "./NewsDetail.css";
import { useNavigate, useParams } from "react-router-dom";
import zmLogo from "./images/LOGO_ZM.png";
import facebookIcon from "./images/facebook-icon.png";
import instagramIcon from "./images/instagram-icon.png";
import twitterIcon from "./images/twitter-icon.png";
import Whatssapp from "./images/whatsapp.png";

// ─── Helpers de contenido (nivel módulo para reutilizar) ─────────────────────

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

function escapeAttr(s) {
  return String(s || '')
    .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
    .replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function splitIntoParagraphs(text = '') {
  const normalized = String(text).replace(/\r\n/g, '\n').trim();
  if (!normalized) return '';
  return normalized.split(/\n{2,}/g).map(block => {
    const sentences = block.split(/([.?!])\s+/g)
      .reduce((acc, part, _idx, arr) => {
        if (part === '.' || part === '!' || part === '?') {
          const prev = acc.pop() || '';
          acc.push((prev + part).trim());
        } else {
          acc.push(part.trim());
        }
        return acc;
      }, [])
      .filter(s => s && s.trim() !== '');
    return sentences
      .map(s => { const t = s.trim(); return /[.?!]$/.test(t) ? t : t + '.'; })
      .map(s => `<p>${escapeHtml(s)}</p>`)
      .join('');
  }).join('');
}

function blockToHtml(block) {
  switch (block.type) {
    case 'title':    return `<h2>${escapeHtml(block.content || '')}</h2>`;
    case 'subtitle': return `<h3>${escapeHtml(block.content || '')}</h3>`;
    case 'text':     return splitIntoParagraphs(block.content || '');
    case 'image':
      return `<figure class="content-image">
        <img src="${escapeAttr(block.media_url || '')}" alt="${escapeAttr(block.alt_text || '')}" />
        ${block.content ? `<figcaption>${escapeHtml(block.content)}</figcaption>` : ''}
      </figure>`;
    case 'video':
      return `<div class="content-video">
        <iframe src="${escapeAttr(block.media_url || '')}" frameBorder="0" allowFullScreen></iframe>
        ${block.content ? `<p class="video-caption">${escapeHtml(block.content)}</p>` : ''}
      </div>`;
    default: return '';
  }
}

// ─── Componente para embeds de Twitter/X ─────────────────────────────────────

function TwitterEmbed({ content }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Insertar solo el <blockquote>; el <script> del embed no ejecuta en React
    containerRef.current.innerHTML = content.replace(/<script[\s\S]*?<\/script>/gi, '').trim();

    const activate = () => {
      if (window.twttr?.widgets?.load) {
        window.twttr.widgets.load(containerRef.current);
      }
    };

    if (window.twttr?.widgets?.load) {
      activate();
      return;
    }

    let scriptEl = document.getElementById('twitter-widget-js');
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.id = 'twitter-widget-js';
      scriptEl.src = 'https://platform.twitter.com/widgets.js';
      scriptEl.async = true;
      scriptEl.charset = 'utf-8';
      document.body.appendChild(scriptEl);
    }
    scriptEl.addEventListener('load', activate);
    return () => scriptEl.removeEventListener('load', activate);
  }, [content]);

  return <div ref={containerRef} className="content-embed" />;
}

// ─────────────────────────────────────────────────────────────────────────────

export default function NewsDetail() {
  const navigate = useNavigate();
  const { slug } = useParams(); // Obtener slug de la URL

  const [newsData, setNewsData] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [relatedTitle, setRelatedTitle] = useState('Noticias Relacionadas');

  // Configuración de la API - usar variable de entorno si la tienes
  const API_BASE = process.env.REACT_APP_API_BASE || "https://backend-zonamicroondas.vercel.app/api";

  // Formateo de fecha
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const months = [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  // Scroll al top cuando cambia el slug
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [slug]);


  // Fetch por slug
  useEffect(() => {
    const fetchNewsDetail = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE}/news/slug/${encodeURIComponent(slug)}`);
        const data = await response.json();

        if (!data.success) {
          throw new Error('Noticia no encontrada');
        }

        const news = data.data;

        const mappedNews = {
          id: news.id,
          title: news.title,
          subtitle: news.subtitle,
          summary: news.summary,
          date: news.published_at,
          author: news.author_name,
          author_email: news.author_email,
          category: news.category_name,
          category_slug: news.category_slug,
          canonical_slug: news.canonical_slug,
          tags: Array.isArray(news.tags) ? news.tags.map(t => (t?.name ? t.name : (t?.tag || t))) : [],
          images: Array.isArray(news.images) ? news.images.filter(Boolean) : [],
          blocks: Array.isArray(news.blocks) ? news.blocks : [],
          related: Array.isArray(news.related) ? news.related : [],
          main_category_id: news.main_category_id
        };

        setNewsData(mappedNews);

        // Helper para mapear artículos relacionados
        const mapArticle = n => ({
          id: n.id,
          slug: n.canonical_slug,
          title: n.title,
          summary: n.summary,
          date: n.published_at,
          image: n.image_url || (n.images && n.images[0] && n.images[0].url) || '/images/default-news.jpg',
          category: n.category_name || '',
          category_slug: n.category_slug || ''
        });

        // Obtener noticias relacionadas
        if (news.related && news.related.length > 0) {
          const relatedWithSlugs = news.related
            .filter(rel => rel.news && rel.news.canonical_slug)
            .slice(0, 3)
            .map(rel => ({
              id: rel.news.id,
              slug: rel.news.canonical_slug,
              title: rel.news.title,
              summary: rel.news.summary,
              date: rel.news.published_at,
              image: (rel.news.images && rel.news.images[0] && rel.news.images[0].url) || '/images/default-news.jpg',
              category: rel.news.category_name || '',
              category_slug: rel.news.category_slug || ''
            }));
          setRelatedTitle('Noticias Relacionadas');
          setRelatedNews(relatedWithSlugs);
        } else {
          // Intentar por categoría
          let found = false;
          const catParam = news.category_slug
            ? `category=${encodeURIComponent(news.category_slug)}`
            : news.main_category_id ? `category_id=${news.main_category_id}` : null;

          if (catParam) {
            try {
              const catRes = await fetch(`${API_BASE}/news?${catParam}&status=published&limit=4&order=published_at&orderDirection=desc`);
              const catJson = await catRes.json();
              if (catJson.success) {
                const mapped = catJson.data.filter(n => n.id !== news.id).slice(0, 3).map(mapArticle);
                if (mapped.length > 0) {
                  setRelatedTitle(`Más de ${news.category_name || 'esta sección'}`);
                  setRelatedNews(mapped);
                  found = true;
                }
              }
            } catch { /* sigue al fallback */ }
          }

          if (!found) {
            // Fallback: noticias más recientes
            try {
              const recentRes = await fetch(`${API_BASE}/news?status=published&limit=4&order=published_at&orderDirection=desc`);
              const recentJson = await recentRes.json();
              if (recentJson.success) {
                const mapped = recentJson.data.filter(n => n.id !== news.id).slice(0, 3).map(mapArticle);
                setRelatedTitle('Lo más reciente');
                setRelatedNews(mapped);
              }
            } catch { setRelatedNews([]); }
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching news detail:", err);
        setError(err.message || "Error al obtener la noticia");
        setLoading(false);
      }
    };

    if (slug) fetchNewsDetail();
    else {
      setLoading(false);
      setError("Slug inválido");
    }
  }, [slug, API_BASE]);

  // -------------------------
  // renderContent: JSX interleaving HTML segments + TwitterEmbed components
  // -------------------------
  const renderContent = () => {
    if (!newsData) return null;

    const summaryHtml = splitIntoParagraphs(newsData.summary || '');
    const sortedBlocks = [...(newsData.blocks || [])].sort((a, b) => (a.position || 0) - (b.position || 0));

    if (sortedBlocks.length === 0) {
      return <div className="article-content" dangerouslySetInnerHTML={{ __html: summaryHtml }} />;
    }

    // Agrupar bloques en segmentos: html acumulado o embed individual
    const segments = [];
    let htmlAccum = summaryHtml;

    for (const block of sortedBlocks) {
      if (block.type === 'embed') {
        if (htmlAccum) { segments.push({ type: 'html', html: htmlAccum }); htmlAccum = ''; }
        segments.push({ type: 'embed', content: block.content, id: block.id ?? block.position });
      } else {
        htmlAccum += blockToHtml(block);
      }
    }
    if (htmlAccum) segments.push({ type: 'html', html: htmlAccum });

    return (
      <>
        {segments.map((seg, i) =>
          seg.type === 'embed'
            ? <TwitterEmbed key={`embed-${seg.id ?? i}`} content={seg.content} />
            : <div key={`html-${i}`} className="article-content" dangerouslySetInnerHTML={{ __html: seg.html }} />
        )}
      </>
    );
  };

  // -------------------------
  // Imágenes principales y adicionales
  // -------------------------
  const mainImage = newsData?.images?.length > 0
    ? newsData.images.find(img => img.position === 0) || newsData.images[0]
    : null;

  const additionalImages = newsData?.images?.length > 1
    ? newsData.images.slice(1)
    : [];

  // Reset índice si cambian las imágenes adicionales
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [additionalImages.length]);

  // Slider: next/prev usando additionalImages.length (corregido)
  const nextImage = () => {
    if (additionalImages.length > 0) {
      setCurrentImageIndex(prev => (prev + 1) % additionalImages.length);
    }
  };

  const prevImage = () => {
    if (additionalImages.length > 0) {
      setCurrentImageIndex(prev => (prev - 1 + additionalImages.length) % additionalImages.length);
    }
  };

  const goToImage = (index) => {
    if (index >= 0 && index < additionalImages.length) setCurrentImageIndex(index);
  };

  // Navegación a noticia relacionada usando category_slug + slug
  const handleRelatedNewsNavigation = (article) => {
    if (article.category_slug && article.slug) {
      navigate(`/${article.category_slug}/articulos/${article.slug}`);
    } else if (article.slug) {
      navigate(`/news/${article.slug}`);
    } else {
      console.warn("Artículo relacionado sin slug:", article);
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  const handleHomeNavigation = () => {
    navigate("/");
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  const handleGoBack = () => navigate(-1);

  // Loading / Error UI
  if (loading) {
    return (
      <div className="news-detail-container">
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <h2>Cargando noticia...</h2>
        </div>
      </div>
    );
  }

  if (error || !newsData) {
    return (
      <div className="news-detail-container">
        <div className="error-message" style={{ textAlign: 'center', padding: '100px 20px' }}>
          <h2>Noticia no encontrada</h2>
          <p>{error || 'La noticia que buscas no existe o ha sido eliminada.'}</p>
          <button onClick={handleHomeNavigation} className="btn-back">
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  // Compartir URL codificada
  const currentUrl = encodeURIComponent(window.location.href);
  const currentTitle = encodeURIComponent(newsData.title || '');

  return (
    <div className="news-detail-container">

      {/* ══ HEADER + TOPBAR sticky — igual estructura que Home ══ */}
      <div className="nd-site-top">
        <header className="header-pro">
          <div className="header-pro-inner">
            <button className="header-logo-btn" onClick={handleHomeNavigation} aria-label="Ir al inicio">
              <img src={zmLogo} alt="Zona Microondas" />
            </button>
            <div className="header-brand">
              <span className="header-brand-title">Querétaro</span>
              <span className="header-brand-sub">LA NOTICIA AL MOMENTO</span>
            </div>
            <div className="header-right">
              <div className="header-socials">
                <a href="https://www.facebook.com/ZonaMicroondas" className="h-soc"
                   target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <img src={facebookIcon} alt="" />
                </a>
                <a href="https://www.instagram.com/zonamicroondasqro" className="h-soc"
                   target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <img src={instagramIcon} alt="" />
                </a>
                <a href="https://twitter.com" className="h-soc"
                   target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                  <img src={twitterIcon} alt="" />
                </a>
              </div>
            </div>
          </div>
        </header>

        {/* Barra roja — igual que la navbar de Home, con Volver a la derecha */}
        <div className="nd-topbar">
          <button className="nd-topbar-back" onClick={handleGoBack}>
            ← Volver
          </button>
        </div>
      </div>

      <main className="detail-main">
        <article className="news-article nd-card">
          <nav className="nd-breadcrumb">
            <span onClick={handleHomeNavigation} className="nd-bc-link">Inicio</span>
            <span className="nd-bc-sep">›</span>
            <span className="nd-bc-current">{newsData.category}</span>
          </nav>

          <header className="article-header">
            <div className="article-meta">
              <span className="article-category">{newsData.category}</span>
              <span className="article-date">{formatDate(newsData.date)}</span>
              <span className="article-author">Por {newsData.author}</span>
            </div>

            <h1 className="article-title">{newsData.title}</h1>
            {newsData.subtitle && <h2 className="article-subtitle">{newsData.subtitle}</h2>}

            {newsData.tags && newsData.tags.length > 0 && (
              <div className="article-tags">
                {newsData.tags.map((tag, index) => <span key={index} className="tag">#{tag}</span>)}
              </div>
            )}
          </header>

          {mainImage && (
            <div className="article-image-container">
              <img
                src={mainImage.url || '/images/default-news.jpg'}
                alt={mainImage.alt_text || newsData.title}
                className="article-image"
              />
              {mainImage.caption && <p className="image-caption">{mainImage.caption}</p>}
            </div>
          )}

          <div className="art-send-bar">
            <span className="art-send-lbl">Compartir:</span>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="art-send-item art-send-blue"
              aria-label="Compartir en Facebook"
            >
              <img src={facebookIcon} alt="" aria-hidden="true" />
              <span>Facebook</span>
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${currentUrl}&text=${currentTitle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="art-send-item art-send-dark"
              aria-label="Compartir en Twitter"
            >
              <img src={twitterIcon} alt="" aria-hidden="true" />
              <span>Twitter</span>
            </a>
            <a
              href={`https://wa.me/?text=${currentTitle}%20${currentUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="art-send-item art-send-green"
              aria-label="Compartir en WhatsApp"
            >
              <img src={Whatssapp} alt="" aria-hidden="true" />
              <span>WhatsApp</span>
            </a>
          </div>

          {renderContent()}

          {additionalImages.length > 0 && (
            <div className="article-gallery-slider">
              <h3>Galería de imágenes</h3>
              <div className="slider-container">
                <button className="slider-btn prev" onClick={prevImage} aria-label="Imagen anterior">‹</button>

                <div className="slider-image-wrapper">
                  <img
                    src={additionalImages[currentImageIndex]?.url || '/images/default-news.jpg'}
                    alt={additionalImages[currentImageIndex]?.alt_text || newsData.title}
                    className="slider-image"
                  />
                  {additionalImages[currentImageIndex]?.caption && <p className="slider-caption">{additionalImages[currentImageIndex].caption}</p>}
                </div>

                <button className="slider-btn next" onClick={nextImage} aria-label="Imagen siguiente">›</button>
              </div>

              <div className="slider-dots">
                {additionalImages.map((_, index) => (
                  <button
                    key={index}
                    className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => goToImage(index)}
                    aria-label={`Ir a imagen ${index + 1}`}
                  />
                ))}
              </div>

              <div className="slider-counter">{currentImageIndex + 1} / {additionalImages.length}</div>
            </div>
          )}

          <footer className="article-footer">
            <div className="article-actions">
              <button onClick={handleGoBack} className="btn-back-main">← Volver</button>
            </div>
          </footer>
        </article>

        <aside className="nd-sidebar">

          {/* Related / recent news */}
          <div className="rel-widget">
            <div className="rel-widget-hdr">
              <div className="rel-accent-bar"></div>
              <h3 className="rel-widget-title">{relatedTitle}</h3>
            </div>
            <div className="rel-list">
              {relatedNews.length > 0 ? relatedNews.map(article => (
                <article key={article.id || article.slug}
                         className="rel-card"
                         onClick={() => handleRelatedNewsNavigation(article)}>
                  <div className="rel-card-thumb">
                    <img src={article.image} alt={article.title} loading="lazy" />
                  </div>
                  <div className="rel-card-info">
                    {article.category && <span className="rel-card-cat">{article.category}</span>}
                    <h4 className="rel-card-title">{article.title}</h4>
                    <time className="rel-card-date">{formatDate(article.date)}</time>
                  </div>
                </article>
              )) : (
                <p className="rel-empty">Cargando...</p>
              )}
            </div>
          </div>

          {/* Síguenos */}
          <div className="nd-social-widget">
            <div className="rel-widget-hdr">
              <div className="rel-accent-bar"></div>
              <h3 className="rel-widget-title">Síguenos</h3>
            </div>
            <div className="nd-social-list">
              <a href="https://www.facebook.com/ZonaMicroondas" className="nd-soc-item nd-soc-fb" target="_blank" rel="noopener noreferrer">
                <img src={facebookIcon} alt="" aria-hidden="true" />
                <div>
                  <span className="nd-soc-name">Facebook</span>
                  <span className="nd-soc-handle">@ZonaMicroondas</span>
                </div>
              </a>
              <a href="https://www.instagram.com/zonamicroondasqro/" className="nd-soc-item nd-soc-ig" target="_blank" rel="noopener noreferrer">
                <img src={instagramIcon} alt="" aria-hidden="true" />
                <div>
                  <span className="nd-soc-name">Instagram</span>
                  <span className="nd-soc-handle">@zonamicroondasqro</span>
                </div>
              </a>
              <a href="https://twitter.com" className="nd-soc-item nd-soc-tw" target="_blank" rel="noopener noreferrer">
                <img src={twitterIcon} alt="" aria-hidden="true" />
                <div>
                  <span className="nd-soc-name">Twitter</span>
                  <span className="nd-soc-handle">@ZONAMICROONDAS</span>
                </div>
              </a>
            </div>
          </div>

        </aside>
      </main>

      <footer className="footer">
        <p className="footer-text">
          Copyright © Todos los derechos reservados | ZONAMICROONDAS.
        </p>
      </footer>
    </div>
  );
}
